import React, { useState, useEffect, useRef } from 'react';
import { Form, Input, Button, Checkbox, ConfigProvider, Result, Select, notification, Alert, Radio, DatePicker } from 'antd';
import { CheckCircleFilled, CloseCircleOutlined } from '@ant-design/icons';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import locale from 'antd/es/date-picker/locale/es_ES';
import axios from 'axios';
import changeHeight from '../data/changeHeight';
dayjs.locale('es');

// Formulario para calcular el complemento de brecha de género
const FormComplementoBrechaGenero = () => {
    const [buttonLoading, setButtonLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [primaryColor, setPrimaryColor] = useState('#FFFFFF');
    const [searchParams] = useSearchParams();
    const [form] = Form.useForm();
    const refForm = useRef(0);
    const refResult = useRef(0);
    const [result, setResult] = useState("collapse");
    const [heightResult, setHeightResult] = useState(0);
    const [api, contextHolder] = notification.useNotification();
    const [alertAnoJubilacion, setAlertAnoJubilacion] = useState("");
    const [alertGenero, setAlertGenero] = useState(false);
    const navigate = useNavigate();
    const [periodoJubilacion, setPeriodoJubilacion] = useState("");
    const [anosJubilacion, setAnosJubilacion] = useState([]);
    const [resultadoCalculadora, setResultadoCalculadora] = useState(null);
    const [totalCalculadora, setTotalCalculadora] = useState(null);
    const [fechaJubilacion, setFechaJubilacion] = useState(null);
    const [importeFinal, setImporteFinal] = useState(null);
    // Tabla de importes por año y número de hijos
    const tablaValores = {
        2021: { '1': 27, '2': 54, '3': 81, '4 o más': 108 },
        2022: { '1': 28, '2': 56, '3': 84, '4 o más': 112 },
        2023: { '1': 30.4, '2': 60.8, '3': 91.2, '4 o más': 121.6 },
        2024: { '1': 33.2, '2': 66.4, '3': 99.6, '4 o más': 132.8 },
        2025: { '1': 35.9, '2': 71.8, '3': 107.7, '4 o más': 143.6 },
    };

    /**
     * Calcula el complemento de brecha de género según la fecha de jubilación y número de hijos
     * @param {dayjs} fechaJub - Fecha de jubilación
     * @param {string} hijos - Número de hijos ('1', '2', '3', '4 o más')
     * @returns {object} - { importeMensual, importeTotal }
     */
    const calcularComplementoBrechaGenero = (fechaJub, hijos) => {
        if (!fechaJub || !hijos) return { importeMensual: null, importeTotal: null };

        const anoJub = fechaJub.year();
        const mesJub = fechaJub.month() + 1; // month() es 0-indexado
        const now = dayjs();
        const anoActual = now.year();
        const mesActual = now.month() + 1;

        let importeTotal = 0;
        let importeMensual = null;

        // Validar año y número de hijos en la tabla
        if (!tablaValores[anoJub] || !tablaValores[anoJub][hijos]) {
            return { importeMensual: null, importeTotal: null };
        }

        importeMensual = tablaValores[anoJub][hijos];

        // Jubilación en el año actual
        if (anoJub === anoActual) {
            const mesesTranscurridos = mesActual - mesJub + 1;
            importeTotal += mesesTranscurridos * importeMensual;

            // Paga extra de junio
            if (mesActual >= 6) {
                if (mesJub >= 1 && mesJub <= 5) {
                    const mesesEnPeriodoDevengo = 5 - mesJub + 1;
                    importeTotal += (mesesEnPeriodoDevengo / 6) * importeMensual;
                }
            }
            // Paga extra de diciembre
            if (mesActual >= 12) {
                if (mesJub >= 6 && mesJub <= 11) {
                    const mesesEnPeriodoDevengo = 11 - mesJub + 1;
                    importeTotal += (mesesEnPeriodoDevengo / 6) * importeMensual;
                } else if (mesJub <= 5) {
                    importeTotal += importeMensual;
                }
            }
        }
        // Jubilación en años anteriores
        else {
            // Primer año de jubilación
            const mesesPrimerAno = 12 - mesJub + 1;
            importeTotal += mesesPrimerAno * importeMensual;

            // Paga extra de junio primer año
            if (mesJub >= 1 && mesJub <= 5) {
                const mesesEnPeriodoDevengo = 5 - mesJub + 1;
                importeTotal += (mesesEnPeriodoDevengo / 6) * importeMensual;
            }
            // Paga extra de diciembre primer año
            if (mesJub >= 6 && mesJub <= 11) {
                const mesesEnPeriodoDevengo = 11 - mesJub + 1;
                importeTotal += (mesesEnPeriodoDevengo / 6) * importeMensual;
                console.log("Importe devengo diciembre primer año: ", importeTotal);
            } else if (mesJub <= 5) {
                importeTotal += importeMensual;
            }
            // Años intermedios completos
            for (let y = anoJub + 1; y < anoActual; y++) {
                if (tablaValores[y] && tablaValores[y][hijos]) {
                    const importeMensualAnio = tablaValores[y][hijos];
                    importeTotal += 14 * importeMensualAnio; // 12 mensualidades + 2 extras
                }
            }
            // Año actual
            if (tablaValores[anoActual] && tablaValores[anoActual][hijos]) {
                const importeMensualActual = tablaValores[anoActual][hijos];
                importeTotal += mesActual * importeMensualActual;
                if (mesActual >= 6) importeTotal += importeMensualActual;
                if (mesActual >= 12) importeTotal += importeMensualActual;
            }
        }
        
        return { importeMensual, importeTotal };
    };

    // Notificación de error
    const openNotification = (placement) => {
        api.info({
            message: 'Error inesperado',
            description:
                'Ha ocurrido un error inesparado. Intentelo más tarde. Disculpe las molestias',
            placement,
            icon: (
                <CloseCircleOutlined style={{ color: "#f7151c" }} />
            )
        });
    };

    // Inicializa color y valores desde parámetros de búsqueda
    useEffect(() => {
        if (searchParams.get("primaryColor")) {
            setPrimaryColor('#' + searchParams.get("primaryColor"));
            setResult("none");
            setHeightResult(refResult.current.clientHeight);
        }
        if (searchParams.get("parentUrl")) {
            form.setFieldsValue({ url: searchParams.get("parentUrl") });
        }
    }, [searchParams]);

    // Observa cambios de tamaño del formulario para ajustar el iframe
    useEffect(() => {
        const resizeObserver = new ResizeObserver((entries) => {
            for (let entry of entries) {
                if (entry.target === refForm.current) {
                    changeHeight(entry.target.clientHeight);
                    break;
                }
            }
        });
        resizeObserver.observe(refForm.current);
        return () => {
            resizeObserver.unobserve(refForm.current);
        };
    }, []);

    // Genera años de jubilación válidos
    useEffect(() => {
        const currentYear = new Date().getFullYear();
        const anos = [];
        for (let y = 2021; y <= currentYear; y++) {
            anos.push(y);
        }
        setAnosJubilacion(anos);
    }, []);

    // Calcula resultados cuando cambian los datos clave
    useEffect(() => {
        if (periodoJubilacion === 'despues2021' && fechaJubilacion && form.getFieldValue('hijos')) {
            const hijos = form.getFieldValue('hijos');
            const resultado = calcularComplementoBrechaGenero(fechaJubilacion, hijos);
            setResultadoCalculadora(resultado.importeMensual);
            setTotalCalculadora(resultado.importeTotal);
        } else {
            setResultadoCalculadora(null);
            setTotalCalculadora(null);
        }
    }, [periodoJubilacion, fechaJubilacion, form.getFieldValue('hijos')]);

    // Recibe datos del parent vía postMessage
    window.addEventListener('message', (event) => {
        if (event.data.type === "from-parent") {
            if (event.data.fbp === 'null' || event.data.fbp === null || event.data.fbp === undefined) {
                form.setFieldsValue({ fbp: " " });
            } else {
                form.setFieldsValue({ fbp: event.data.fbp });
            }
            if (event.data.hubspotutk === 'null' || event.data.hubspotutk === null || event.data.hubspotutk === undefined) {
                form.setFieldsValue({ hubspotutk: " " });
            } else {
                form.setFieldsValue({ hubspotutk: event.data.hubspotutk });
            }
            if (event.data.userAgent) { form.setFieldsValue({ userAgent: event.data.userAgent }); }
            changeHeight(refForm.current.clientHeight);
        }
    });

    // Envía el formulario
    const onFinish = (values) => {
        // setButtonLoading(true);
        values.url = values.url + "?brecha-genero"
        values.ano_jubilacion = values.ano_jubilacion.format('MMMM YYYY');
        axios.post(`${process.env.REACT_APP_URL_PRIMA_UNICA}`, values)
            .then(function (response) {
                if (response.data.message === "OK") {
                    setButtonLoading(false);
                    setSuccess(true);
                    setResult("block");
                    changeHeight(heightResult);
                    if (fechaJubilacion && values.hijos) {
                        const resultado = calcularComplementoBrechaGenero(fechaJubilacion, values.hijos);
                        setImporteFinal(resultado.importeTotal);
                    }
                }
            })
            .catch(function () {
                openNotification('bottom');
                setButtonLoading(false);
            });
    };

    // Muestra alerta si la jubilación es antes de 2021
    const onChangeAnoJubilacion = (value) => {
        if (value === "antes2021") {
            setAlertAnoJubilacion('antes2021');
        } else {
            setAlertAnoJubilacion('');
        }
    };

    // Muestra alerta si el género no es hombre
    const onChangeGenero = (value) => {
        if (value !== 'Hombre') {
            setAlertGenero(true);
        } else {
            setAlertGenero(false);
        }
    };

    // Actualiza resultados al cambiar año o hijos
    const handleValuesChange = (changedValues, allValues) => {
        if (changedValues.ano_jubilacion || changedValues.hijos) {
            if (periodoJubilacion === 'despues2021' && allValues.ano_jubilacion && allValues.hijos) {
                const fecha = dayjs(allValues.ano_jubilacion);
                const hijos = allValues.hijos;
                const resultado = calcularComplementoBrechaGenero(fecha, hijos);
                setResultadoCalculadora(resultado.importeMensual);
                setTotalCalculadora(resultado.importeTotal);
            } else {
                setResultadoCalculadora(null);
                setTotalCalculadora(null);
            }
        }
    };

    // Opciones de meses según el año seleccionado (no usado actualmente)
    const getMesesOptions = () => {
        const anoSeleccionado = form.getFieldValue('ano_jubilacion');
        let meses = [
            { key: 1, label: 'Enero' },
            { key: 2, label: 'Febrero' },
            { key: 3, label: 'Marzo' },
            { key: 4, label: 'Abril' },
            { key: 5, label: 'Mayo' },
            { key: 6, label: 'Junio' },
            { key: 7, label: 'Julio' },
            { key: 8, label: 'Agosto' },
            { key: 9, label: 'Septiembre' },
            { key: 10, label: 'Octubre' },
            { key: 11, label: 'Noviembre' },
            { key: 12, label: 'Diciembre' },
        ];
        if (anoSeleccionado === 2021 || anoSeleccionado === '2021') {
            meses = meses.filter(m => m.key !== 1);
        }
        return meses;
    };

    return (
        <>
            {contextHolder}
            <ConfigProvider
                theme={{
                    token: {
                        colorPrimary: primaryColor,
                    },
                }}
            >
                <style>
                    {`
                    ::-webkit-scrollbar {
                        display: none;
                    }
                    `}
                </style>
                <div ref={refForm}>
                    <Form
                        form={form}
                        style={{ padding: '1.5rem' }}
                        hidden={success}
                        className='padding-form'
                        name="brecha-genero"
                        initialValues={{ remember: true }}
                        layout='vertical'
                        onFinish={onFinish}
                        onValuesChange={handleValuesChange}
                    >
                        {/* Campos ocultos para tracking */}
                        <Form.Item hidden={true} label="URL" name="url">
                            <Input type='text' />
                        </Form.Item>
                        <Form.Item hidden={true} label="FB" name="fbp">
                            <Input type='' />
                        </Form.Item>
                        <Form.Item hidden={true} label="Hubspotutk" name="hubspotutk">
                            <Input type='' />
                        </Form.Item>
                        <Form.Item
                            style={{ marginBottom: "1.2rem" }}
                            label={<p style={{ fontSize: "15px", marginTop: "-3px", marginBottom: "3px" }}>¿Cuál es tu género?</p>}
                            name="genero"
                            rules={[{ required: true, message: 'Por favor, escoge una opción' }]}
                        >
                            <Radio.Group disabled={buttonLoading} onChange={e => onChangeGenero(e.target.value)}>
                                <Radio value="Mujer">Mujer</Radio>
                                <Radio value="Hombre">Hombre</Radio>
                            </Radio.Group>
                        </Form.Item>
                        <div style={{ display: `${alertGenero ? "block" : "none"}` }}>
                            <Alert style={{ marginTop: "5%", marginBottom: "5%" }} message="Si eres mujer, la Seguridad Social te habrá aplicado el complemento de brecha de género automáticamente. No es necesario que rellenes este formulario." type="info" />
                        </div>
                        <Form.Item
                            style={{ marginBottom: "1.2rem" }}
                            label={<p style={{ fontSize: "15px", marginTop: "-3px", marginBottom: "3px" }}>¿Cuántos hijos tienes?</p>}
                            name="hijos"
                            rules={[{ required: true, message: 'Por favor, escoge una opción' }]}
                        >
                            <Select showSearch placeholder='Escoge una cifra' disabled={buttonLoading}>
                                <Select.Option key="1" value="1">1</Select.Option>
                                <Select.Option key="2" value="2">2</Select.Option>
                                <Select.Option key="3" value="3">3</Select.Option>
                                <Select.Option key="4 o más" value="4 o más">4 o más</Select.Option>
                            </Select>
                        </Form.Item>
                        <Form.Item
                            style={{ marginBottom: "1.2rem" }}
                            label={<p style={{ fontSize: "15px", marginTop: "-3px", marginBottom: "3px" }}>¿En qué periodo te jubilaste?</p>}
                            name="periodo_jubilacion"
                            rules={[{ required: true, message: 'Por favor, escoge un periodo' }]}
                        >
                            <Select showSearch placeholder='Escoge un periodo' disabled={buttonLoading} onChange={value => { onChangeAnoJubilacion(value); setPeriodoJubilacion(value); }}>
                                <Select.Option key="antes2021" value="antes2021">Antes del 3 de febrero de 2021</Select.Option>
                                <Select.Option key="despues2021" value="despues2021">Después del 3 de febrero de 2021</Select.Option>
                            </Select>
                        </Form.Item>
                        {periodoJubilacion === 'despues2021' && (
                            <>
                                <Form.Item
                                    style={{ marginBottom: "1.2rem" }}
                                    label={<p style={{ fontSize: "15px", marginTop: "-3px", marginBottom: "3px" }}>¿En qué mes y año te jubilaste?</p>}
                                    name="ano_jubilacion"
                                    rules={[{ required: true, message: 'Por favor, selecciona el mes y año de jubilación' }]}
                                    getValueFromEvent={value => value ? dayjs(value) : null}
                                >
                                    <DatePicker
                                        picker="month"
                                        style={{ width: '100%', height: '38px' }}
                                        disabled={buttonLoading}
                                        format="MMMM YYYY"
                                        showToday={false}
                                        renderExtraFooter={() => null}
                                        locale={locale}
                                        onChange={value => {
                                            setFechaJubilacion(value);
                                            form.setFieldsValue({ ano_jubilacion: value });
                                        }}
                                        disabledDate={current => {
                                            const minDate = dayjs('2021-02-01');
                                            const maxDate = dayjs().endOf('month');
                                            return current && (current < minDate || current > maxDate);
                                        }}
                                    />
                                    <div style={{ fontSize: '13px', color: '#888', marginTop: 8, marginBottom: 5 }}>
                                        Puedes hacer clic en el año para cambiarlo y seleccionar cualquier mes desde febrero de 2021.
                                    </div>
                                </Form.Item>
                            </>
                        )}
                        <div style={{ display: `${alertAnoJubilacion === 'antes2021' ? "block" : "none"}` }}>
                            <Alert
                                style={{ marginTop: '5%', marginBottom: '5%' }}
                                message="No puedes acogerte a el complemento de la brecha de género"
                                description={
                                    <span>
                                        Si te jubilaste antes del 3 de febrero de 2021, rellena nuestro formulario para pedir el{' '}
                                        <a href="https://www.coventia.es/complemento-por-paternidad" target="_blank" rel="noopener noreferrer">
                                            complemento de paternidad
                                        </a>.
                                    </span>
                                }
                                type="info"
                            />
                        </div>
                        <Form.Item
                            style={{ marginBottom: "1.2rem" }}
                            label={<p style={{ fontSize: "15px", marginTop: "-3px", marginBottom: "3px" }}>¿Dónde te podemos enviar un email?</p>}
                            name="email"
                            rules={[{ required: true, message: 'Por favor, ingresa tu email' }]}
                        >
                            <Input placeholder='tunombre@email.com' type='email' disabled={buttonLoading} style={{ height: '38px' }} />
                        </Form.Item>
                        <Form.Item
                            style={{ marginBottom: "1.2rem" }}
                            label={<p style={{ fontSize: "15px", marginTop: "-3px", marginBottom: "3px" }}>¿Dónde podemos llamarte?</p>}
                            name="tel"
                            rules={[{ required: true, message: 'Por favor, ingresa tu teléfono' }]}
                        >
                            <Input placeholder='919 99 31 31' type='tel' disabled={buttonLoading} maxLength={9} minLength={9} style={{ height: '38px' }} />
                        </Form.Item>
                        <Form.Item style={{ marginBottom: "1rem" }} valuePropName="checked" name="terms" initialValue={false} rules={[{ required: true, validator: async (_, checked) => { if (!checked) { return Promise.reject(new Error("Por favor, acepta los términos y condiciones")); } } }]}> 
                            <Checkbox style={{ fontSize: "15px" }} disabled={buttonLoading} >Acepto los <Link target='_blank' to='https://www.coventia.es/politica-de-privacidad-y-aviso-de-cookies'>términos y condiciones</Link></Checkbox>
                        </Form.Item>
                        <Button type='primary' loading={buttonLoading} htmlType="submit" style={{ width: "100%", height: "40px", marginTop: "10px" }} disabled={alertAnoJubilacion === 'antes2021' || alertGenero}>
                            {buttonLoading ? 'Enviando...' : 'Enviar'}
                        </Button>
                    </Form>
                </div>
                <div ref={refResult}>
                    <div style={{ display: `${result}`, height: '100vh', width: '100%', position: 'absolute', top: 0, left: 0 }}>
                        <Result
                            icon={<CheckCircleFilled style={{ color: primaryColor, fontSize: 64 }} />}
                            style={{
                                padding: '2.5rem',
                                paddingTop: '3rem',
                                background: '#fff',
                                borderRadius: 16,
                                boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
                                height: '100%',
                                width: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                alignItems: 'center'
                            }}
                            status="success"
                            title={<span style={{ fontSize: 28, fontWeight: 700, color: primaryColor }}>¡Gracias por confiar en nosotros!</span>}
                            subTitle={
                                <div style={{ marginTop: 24, textAlign: 'center', width: '100%' }}>
                                    <div style={{ fontSize: 22, fontWeight: 600, color: '#222', marginBottom: 12 }}>
                                        {importeFinal !== null && fechaJubilacion && (
                                            <>
                                                Importe total estimado a percibir<br />
                                                <span style={{ fontSize: 18, color: '#888', fontWeight: 400 }}>
                                                    (desde {fechaJubilacion.format('MMMM YYYY')})
                                                </span><br />
                                                <span style={{ fontSize: 36, color: primaryColor, fontWeight: 800 }}>
                                                    {importeFinal.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}
                                                </span>
                                            </>
                                        )}
                                    </div>
                                    <div style={{ fontSize: 18, color: '#555', marginTop: 16 }}>
                                        En breve nos pondremos en contacto contigo para ayudarte a conseguirlo.
                                    </div>
                                </div>
                            }
                        />
                    </div>
                </div>
            </ConfigProvider >
        </>
    );
}

export default FormComplementoBrechaGenero;
